import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Employee } from '../../../dtos/employee';
import { AjaxResult } from '../../../enums/ajaxResult';
import { NewEmployeeForm } from './form';
import { Button } from '@material-ui/core';
import { PageHeader } from '../../PageHeader';
import { ApiResult } from '../../../rest/baseApi';
import { SortInfo } from '../../../rest/requestObjects/SortInfo';
import { OrgSelect } from '../../shared/OrgSelect';
import { useApi } from '../../useApi';
import { useOrgSiteContext } from '../../../hooks/useOrgSiteContext';
import { EmployeesRequest } from '../../../rest/requestObjects/employeesRequest';
import { UserLevel } from '../../../enums/userLevel';
import { HelpVideo } from '../../../enums/helpVideo';
import { ButtonsContainer } from '../../shared/ButtonsContainer';

import { useAuth } from "../../../features/auth/context";
import { PaginationControls } from "../../PaginationControls";
import { SearchBar } from "../../SearchBar";

import { AccountTypeSelect } from "./AccountTypeSelect";
import { EmployeesExportButton } from "./EmployeesExportButton";
import { EmployeeStatusSelect } from "./EmployeeStatusSelect";
import { EmployeesTable } from "./table";
import { useIsFederatedSsoEnabled } from './useIsFederatedSsoEnabled';

export const EmployeesPage = () => {
    const { dealerId, siteChangeCallback, siteId, orgId } = useOrgSiteContext('employees');
    const isFederatedSsoEnabled = useIsFederatedSsoEnabled(orgId);
    const {
        isGlobalAdmin,
        isDealerAdmin,
        isDealerEmployee,
        isOrgAdmin,
        isOrgEmployee,
        isSiteAdmin,
        isSiteEmployee,
        isReadOnlyUser,
    } = useAuth();
    const { employeeApi } = useApi();
    const isMountedRef = useRef(true);
    useEffect(() => () => { isMountedRef.current = false; }, []);

    const deassocciateSiteFromContext = isOrgAdmin || isOrgEmployee || isSiteAdmin || isSiteEmployee;

    const [employees, setEmployees] = useState<Employee[]>([]);
    const [newEmployeeFormIsVisible, setNewEmployeeFormIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [page, setPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(500);
    const [numberOfPages, setNumberOfPages] = useState(0);
    const [searchText, setSearchText] = useState<string>('');
    const [sortInfo, setSortInfo] = useState<SortInfo>();
    const [accountTypeFilter, setAccountTypeFilter] = useState<UserLevel>();
    const [isInactiveFilter, setIsInactiveFilter] = useState<boolean>();
    const search = useCallback((text: string) => setSearchText(text), []);
    const options: EmployeesRequest = useMemo(() => ({
        itemsPerPage,
        page: page + 1,
        searchText: searchText,
        sortInfo: sortInfo,
        roles: accountTypeFilter ? [accountTypeFilter] : undefined,
        isInactive: isInactiveFilter,
    }), [
        page,
        itemsPerPage,
        searchText,
        sortInfo,
        accountTypeFilter,
        isInactiveFilter,
    ]);

    const loadEmployees = useCallback(() => {
        let promise: Promise<ApiResult<{ employees: Employee[]; numberOfPages: number }>>;
        if (orgId && siteId) {
            promise = employeeApi.getEmployeesForSite(orgId, siteId, options);
        } else if (orgId) {
            promise = employeeApi.getEmployeesForOrg(orgId, options);
        } else if (dealerId) {
            promise = employeeApi.getEmployeesForDealer(dealerId, options);
        } else {
            promise = employeeApi.getAllEmployees(options);
        }
        setIsLoading(true);
        promise.then((r) => {
            if (!isMountedRef.current) return;
            setIsLoading(false);
            if (r.result === AjaxResult.Success && r.data) {
                setEmployees(r.data.employees);
                setNumberOfPages(r.data.numberOfPages);
            }
        });
    }, [employeeApi, orgId, siteId, dealerId, options]);

    useEffect(() => {
        loadEmployees();
    }, [loadEmployees]);


    const resetFilters = useCallback(() => {
        setAccountTypeFilter(undefined);
        setIsInactiveFilter(undefined);
        setSortInfo(undefined);
    }, []);

    return (
        <>
            <PageHeader
                helpVideoNum={HelpVideo.Employee}
                isLoading={isLoading}
            >
                Employees
            </PageHeader>

            <OrgSelect
                selectedDealerId={dealerId}
                selectedOrgId={orgId}
                selectedSiteId={siteId || 0}
                dealerSelectIsEnabled={isGlobalAdmin}
                orgSelectIsEnabled={isGlobalAdmin || isDealerAdmin || isDealerEmployee}
                includeAllSites
                bypassSiteContext={deassocciateSiteFromContext}
                onChange={(val: {
                    dealerId?: number | null;
                    orgId?: number | null;
                    siteId?: number | null;
                }) => siteChangeCallback(val.dealerId, val.orgId, val.siteId)}
            />

            <SearchBar
                defaultSearchText={searchText}
                search={search}
                labelText="Search Employees..."
                onReset={resetFilters}
            />

            <AccountTypeSelect
                value={accountTypeFilter}
                setValue={setAccountTypeFilter}
                isDealerSelected={Boolean(dealerId)}
                isOrgSelected={Boolean(orgId)}
                isSiteSelected={Boolean(siteId)}
            />

            <EmployeeStatusSelect
                isInactive={isInactiveFilter}
                setValue={setIsInactiveFilter}
            />

            <ButtonsContainer>
                {!isFederatedSsoEnabled && !isReadOnlyUser && (
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => setNewEmployeeFormIsVisible(true)}
                    >
                        Register New
                    </Button>
                )}

                <EmployeesExportButton
                    orgId={orgId}
                    siteId={siteId}
                    dealerId={dealerId}
                    options={options}
                />
            </ButtonsContainer>

            <PaginationControls
                page={page}
                setPage={setPage}
                pageCount={numberOfPages}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                disabled={isLoading}
            />

            <EmployeesTable
                dealerId={dealerId}
                orgId={orgId}
                siteId={siteId}
                employees={employees}
                onLoadEmployees={loadEmployees}
                sortInfo={sortInfo}
                setSortInfo={setSortInfo}
                isFederatedSsoEnabled={isFederatedSsoEnabled}
                isLoading={isLoading}
            />

            {newEmployeeFormIsVisible ? (
                <NewEmployeeForm
                    {...{ siteId, orgId, dealerId }}
                    closeForm={() => setNewEmployeeFormIsVisible(false)}
                    loadEmployees={loadEmployees}
                />
            ) : null}
        </>
    );
};
